// src/pages/Dashboard.jsx
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle, Wrench, FileClock, BarChart3, Clock, Calendar, Moon,
  PlusCircle, UserPlus, PackagePlus, Building
} from 'lucide-react';

import { auth, db } from '../firebaseConfig';
import { useEmpresaId } from '../context/EmpresaContext';

/* ---------- Cards ---------- */
const IndicadorCard = ({ icone, titulo, valor, cor, onClick }) => {
  const IconeComponente = icone;
  const cardClasses =
    `relative overflow-hidden bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg flex flex-col justify-between ${
      onClick ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300' : ''
    }`;

  return (
    <div className={cardClasses} onClick={onClick}>
      <div className={`absolute -top-4 -right-4 w-20 h-20 rounded-full opacity-10 ${cor}`}></div>
      <div>
        <div className="flex items-start justify-between">
          <div className={`p-3 rounded-full ${cor} bg-opacity-10`}>
            <IconeComponente className={`w-7 h-7 ${cor}`} />
          </div>
        </div>
        <p className="text-3xl font-bold text-gray-800 dark:text-gray-100 mt-4">{valor}</p>
        <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mt-1">{titulo}</h3>
      </div>
    </div>
  );
};

const EstoqueBaixoCard = ({ produtos, loading, onClick }) => {
  const cardClasses =
    `bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg col-span-1 lg:col-span-2 ${
      onClick ? 'cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300' : ''
    }`;

  return (
    <div className={cardClasses} onClick={onClick}>
      <div className="flex items-center mb-4">
        <AlertTriangle className="w-6 h-6 text-yellow-500 mr-3" />
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Produtos com estoque baixo</h3>
      </div>
      <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
        {loading ? (
          <p className="text-gray-500 dark:text-gray-400">A carregar...</p>
        ) : produtos.length > 0 ? (
          produtos.map(produto => (
            <div
              key={produto.id}
              className="flex justify-between items-center text-sm p-2 rounded-md bg-gray-50 dark:bg-gray-700/50"
            >
              <span className="text-gray-700 dark:text-gray-300">{produto.nome}</span>
              <span className="font-bold text-yellow-600 dark:text-yellow-400">
                Restam: {produto.quantidade}
              </span>
            </div>
          ))
        ) : (
          <p className="text-gray-500 dark:text-gray-400 text-center py-4">
            Nenhum produto com estoque baixo.
          </p>
        )}
      </div>
    </div>
  );
};

const AtalhoRapidoCard = ({ icone, titulo, onClick }) => {
  const Icone = icone;
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center justify-center text-center p-4 bg-white dark:bg-gray-800 rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
    >
      <div className="p-4 bg-blue-500/10 rounded-full text-blue-600 dark:bg-blue-400/10 dark:text-blue-400">
        <Icone size={28} />
      </div>
      <p className="mt-3 font-semibold text-sm text-gray-700 dark:text-gray-300">{titulo}</p>
    </button>
  );
};

/* ---------- Página ---------- */
export default function Dashboard() {
  const empresaId = useEmpresaId();
  const navigate = useNavigate();

  const [indicadores, setIndicadores] = useState({
    osAbertas: 0,
    orcamentosPendentes: 0,
    atendimentosHoje: 0,
    atendimentosSemana: 0,
    atendimentosMes: 0
  });
  const [produtosEstoqueBaixo, setProdutosEstoqueBaixo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) setLoading(false);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser || !empresaId) {
        setLoading(false);
        return;
      }
      setLoading(true);

      // Nomes de campos
      const CAMPO_STATUS = 'status';
      const CAMPO_DATA = 'dataAbertura';
      const STATUS_CONCLUIDA = 'Finalizada';
      const STATUS_ORCAMENTO = 'Orçamento';

      const CAMPO_NOME_PRODUTO = 'nome';
      const CAMPO_QTD_PRIMARY = 'estoque';     // tentativa 1
      const CAMPO_QTD_FALLBACK = 'quantidade'; // tentativa 2
      const LIMITE_ESTOQUE_BAIXO = 5;

      try {
        /* ---- OS: subcoleção correta ---- */
        // Regras permitem: /empresas/{empresaId}/os/**
        const osRef = collection(db, 'empresas', empresaId, 'os');
        const osSnap = await getDocs(osRef);
        const todasAsOS = osSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const osConcluidas = todasAsOS.filter(os => os[CAMPO_STATUS] === STATUS_CONCLUIDA);
        const orcamentosPendentes = todasAsOS.filter(os => os[CAMPO_STATUS] === STATUS_ORCAMENTO).length;
        const osAbertas = todasAsOS.filter(
          os => os[CAMPO_STATUS] !== STATUS_CONCLUIDA && os[CAMPO_STATUS] !== STATUS_ORCAMENTO
        ).length;

        const hoje = new Date();
        const inicioDoDia = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
        const inicioDaSemana = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() - hoje.getDay());

        let contHoje = 0, contSemana = 0, contMes = 0;

        osConcluidas.forEach(os => {
          const ts = os[CAMPO_DATA];
          const dataOS = ts && typeof ts.toDate === 'function' ? ts.toDate() : null;
          if (!dataOS) return;
          if (dataOS >= inicioDoDia) contHoje++;
          if (dataOS >= inicioDaSemana) contSemana++;
          if (dataOS.getMonth() === hoje.getMonth() && dataOS.getFullYear() === hoje.getFullYear()) contMes++;
        });

        setIndicadores({
          osAbertas,
          orcamentosPendentes,
          atendimentosHoje: contHoje,
          atendimentosSemana: contSemana,
          atendimentosMes: contMes
        });

        /* ---- Produtos: tenta top-level (legado) e cai para subcoleção se necessário ---- */
        let produtosDocs = [];
        try {
          const qTop = query(collection(db, 'produtos'), where('empresaId', '==', empresaId));
          const snapTop = await getDocs(qTop);
          produtosDocs = snapTop.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (e) {
          console.warn('[Dashboard] Sem permissão/índice para produtos top-level, tentando subcoleção…', e);
        }

        // fallback se vazio (ou se preferir sempre complementar)
        if (produtosDocs.length === 0) {
          try {
            const subRef = collection(db, 'empresas', empresaId, 'produtos');
            const snapSub = await getDocs(subRef);
            produtosDocs = snapSub.docs.map(d => ({ id: d.id, ...d.data() }));
          } catch (e2) {
            console.warn('[Dashboard] Sem permissão para produtos na subcoleção também.', e2);
          }
        }

        // Normaliza quantidade (aceita 'estoque' ou 'quantidade')
        const produtosBaixo = produtosDocs
          .map(p => {
            const qtd = Number.isFinite(p[CAMPO_QTD_PRIMARY])
              ? p[CAMPO_QTD_PRIMARY]
              : p[CAMPO_QTD_FALLBACK];
            return {
              id: p.id,
              nome: p[CAMPO_NOME_PRODUTO] || "",
              quantidade: parseInt(qtd, 10)
            };
          })
          .filter(p => Number.isFinite(p.quantidade) && p.quantidade <= LIMITE_ESTOQUE_BAIXO);

        // Ordenar alfabeticamente por nome (ignora acentos e considera números)
        const produtosBaixoOrdenados = [...produtosBaixo].sort((a, b) => {
          const na = (a.nome || "").toString();
          const nb = (b.nome || "").toString();
          if (!na && !nb) return 0;
          if (!na) return 1;
          if (!nb) return -1;
          return na.localeCompare(nb, "pt-BR", { sensitivity: "base", numeric: true });
        });

        setProdutosEstoqueBaixo(produtosBaixoOrdenados);
      } catch (error) {
        console.error('Erro ao procurar dados do dashboard:', error);
      }

      setLoading(false);
    };

    fetchData();
  }, [currentUser, empresaId]);

  if (loading)
    return <div className="p-6 md:p-8 text-center text-gray-500 dark:text-gray-400">A carregar indicadores...</div>;
  if (!empresaId)
    return <div className="p-6 md:p-8 text-center text-gray-500 dark:text-gray-400">Por favor, selecione uma empresa para ver os indicadores.</div>;

  return (
    <div className="p-6 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">
          Bem-vindo, {auth.currentUser ? auth.currentUser.email.split('@')[0] : 'Visitante'}!
        </h1>
        <p className="mt-1 text-md text-gray-600 dark:text-gray-400">Aqui está um resumo do movimento da sua oficina.</p>
      </div>

      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">Atalhos Rápidos</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
          <AtalhoRapidoCard icone={PlusCircle} titulo="Nova OS" onClick={() => navigate('/os')} />
          <AtalhoRapidoCard icone={UserPlus} titulo="Novo Cliente" onClick={() => navigate('/clientes')} />
          <AtalhoRapidoCard icone={PackagePlus} titulo="Novo Produto" onClick={() => navigate('/produtos')} />
          <AtalhoRapidoCard icone={Building} titulo="Minha Empresa" onClick={() => navigate('/empresas')} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <IndicadorCard icone={Wrench} titulo="OS em Aberto" valor={indicadores.osAbertas} cor="text-blue-500" onClick={() => navigate('/os')} />
        <IndicadorCard icone={FileClock} titulo="Orçamentos Pendentes" valor={indicadores.orcamentosPendentes} cor="text-orange-500" onClick={() => navigate('/orcamentos')} />
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg flex flex-col justify-between">
          <div>
            <div className="flex items-center mb-4">
              <div className="p-3 bg-green-500/10 rounded-full">
                <BarChart3 className="w-7 h-7 text-green-500" />
              </div>
              <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400 ml-3">ATENDIMENTOS CONCLUÍDOS</h3>
            </div>
            <div className="space-y-3 text-base">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400 flex items-center">
                  <Clock size={16} className="mr-2" />
                  Hoje
                </span>
                <span className="font-bold text-lg text-gray-800 dark:text-gray-100">{indicadores.atendimentosHoje}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400 flex items-center">
                  <Calendar size={16} className="mr-2" />
                  Esta Semana
                </span>
                <span className="font-bold text-lg text-gray-800 dark:text-gray-100">{indicadores.atendimentosSemana}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400 flex items-center">
                  <Moon size={16} className="mr-2" />
                  Este Mês
                </span>
                <span className="font-bold text-lg text-gray-800 dark:text-gray-100">{indicadores.atendimentosMes}</span>
              </div>
            </div>
          </div>
        </div>
        <EstoqueBaixoCard
          produtos={produtosEstoqueBaixo}
          loading={loading}
          onClick={() => navigate('/produtos')}
        />
      </div>
    </div>
  );
}
